import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Clock, Eye } from "lucide-react";
import MessageDetailsModal from "./MessageDetailsModal"; // You'll need to create or import this

const RecentMessages = ({ recentMessages }) => {
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const tableVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: 0.4,
      },
    },
  };

  // Handle view action - opens the modal
  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMessage(null);
  };

  // Format message data for the modal
  const formatMessageForModal = (message) => {
    return {
      id: message.id,
      sender_id: message.senderId,
      sms_type: message.type || "SMS",
      date: message.time,
      status: message.status,
      sms: {
        snd_txt: message.message,
        snd_to: message.senderName || "Unknown",
        required_units: 1, // Default value
      },
      total_sent: 1, // Individual message
    };
  };

  return (
    <>
      <motion.div
        className="bg-white rounded-xl border border-gray-200 shadow-sm"
        variants={tableVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Recent SMS Messages</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Sender
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  ID
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Message
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentMessages.map((message, index) => (
                <motion.tr
                  key={index}
                  className="hover:bg-gray-50"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{
                    scale: 1.01,
                    transition: { duration: 0.2 },
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-semibold text-gray-900">
                        {message.senderName}
                      </div>
                      <div className="inline-block px-2 py-1 mt-1 font-mono text-xs text-gray-500 bg-gray-100 rounded-md w-fit">
                        SENDER ID: {message.senderId}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap">
                    <div className="flex justify-center items-center">
                      <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-orange-800 bg-orange-100 border border-orange-200 rounded-full shadow-sm">
                        <span className="mr-2 w-2 h-2 bg-orange-500 rounded-full"></span>
                        #{message.id}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 max-w-xs text-sm text-gray-900 truncate">
                    {message.message}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold ${message.typeColor} rounded-full`}
                    >
                      {message.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold ${message.statusColor} rounded-full`}
                    >
                      {message.status === "Delivered" && (
                        <CheckCircle className="mr-1 w-3 h-3" />
                      )}
                      {message.status === "Failed" && (
                        <XCircle className="mr-1 w-3 h-3" />
                      )}
                      {message.status === "Pending" && (
                        <Clock className="mr-1 w-3 h-3" />
                      )}
                      {message.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                    {message.time}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleViewMessage(message)}
                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                      title="View Message Details"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Modal for viewing message details */}
      {selectedMessage && (
        <MessageDetailsModal
          open={modalOpen}
          onClose={handleCloseModal}
          message={formatMessageForModal(selectedMessage)}
        />
      )}
    </>
  );
};

export default RecentMessages;